import { NmLoader } from '../../components/NmLoader';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from '../../components/NmButton';
import { NmInput } from '../../components/NmInput';
import { getPlatformStore } from '../../platform';
import { comparisons as comparisonsApi } from '../../services/api';
import { formatApiDateCompact } from '../../utils/calendarPickerLocale';
import type { SavedComparisonRecord, ComparisonSession } from './types';
import { COMPARISON_COLORS } from './types';
import { SharedComparisonReport } from './SharedComparisonReport';

interface SavedComparisonsTabProps {
  userId: number;
  refreshToken: number;
  onOpen: (record: SavedComparisonRecord) => void;
}

/** Map API list item shape to local saved record. */
function mapApiComparisonItem(raw: Record<string, unknown>): SavedComparisonRecord {
  const session = (raw.session ?? {}) as ComparisonSession;
  return {
    id: String(raw.id ?? raw.apiId ?? crypto.randomUUID()),
    apiId: typeof raw.id === 'number' ? raw.id : typeof raw.apiId === 'number' ? raw.apiId : undefined,
    name: String(raw.name ?? 'Comparison'),
    tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : [],
    notes: String(raw.notes ?? ''),
    savedAt: String(raw.savedAt ?? raw.created_at ?? new Date().toISOString()),
    session,
    sparklineA: (raw.sparklineA as number[] | undefined) ?? (raw.snapshot as { sparkline_a?: number[] })?.sparkline_a,
    sparklineB: (raw.sparklineB as number[] | undefined) ?? (raw.snapshot as { sparkline_b?: number[] })?.sparkline_b,
    shareToken: (raw.share_token as string | null | undefined) ?? null,
  };
}

function segmentToApi(seg: SavedComparisonRecord['session']['segmentA']) {
  if (!seg) return null;
  return { start_sec: seg.startSec, end_sec: seg.endSec };
}

function MiniSparkline({ a, b }: { a?: number[]; b?: number[] }) {
  const w = 120;
  const h = 32;
  const all = [...(a ?? []), ...(b ?? [])];
  const max = Math.max(...all, 1);
  const path = (pts: number[] | undefined, color: string) => {
    if (!pts?.length) return null;
    const d = pts
      .map((v, i) => {
        const x = (i / Math.max(pts.length - 1, 1)) * w;
        const y = h - (v / max) * h;
        return `${i === 0 ? 'M' : 'L'}${x},${y}`;
      })
      .join(' ');
    return <path d={d} fill="none" stroke={color} strokeWidth={1.5} />;
  };
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      {path(a, COMPARISON_COLORS.a)}
      {path(b, COMPARISON_COLORS.b)}
    </svg>
  );
}

export function SavedComparisonsTab({ userId, refreshToken, onOpen }: SavedComparisonsTabProps) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<SavedComparisonRecord[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sharedToken, setSharedToken] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const local = (await getPlatformStore().getSavedComparisons()) as unknown as SavedComparisonRecord[];
    let merged = [...local];

    try {
      const res = await comparisonsApi.list(userId, { search: search || undefined });
      const rawItems =
        (res.data as { data?: { items?: Record<string, unknown>[] } })?.data?.items ?? [];
      const apiItems = rawItems.map(mapApiComparisonItem);
      const localApiIds = new Set(local.map(l => l.apiId).filter(Boolean));
      for (const item of apiItems) {
        if (!localApiIds.has(item.apiId)) merged.push(item);
      }
      merged.sort((x, y) => (y.savedAt ?? '').localeCompare(x.savedAt ?? ''));

      for (const loc of local) {
        if (!loc.apiId) {
          try {
            const created = await comparisonsApi.create(userId, {
              name: loc.name,
              tags: loc.tags,
              notes: loc.notes,
              workout_a_id: loc.session.workoutA!.id,
              workout_b_id: loc.session.workoutB?.id ?? null,
              segment_a: segmentToApi(loc.session.segmentA),
              segment_b: segmentToApi(loc.session.segmentB),
              config: {
                mode: loc.session.mode,
                viewMode: loc.session.viewMode,
                metric: loc.session.metric,
                alignment: loc.session.alignment,
              },
              sparkline_a: loc.sparklineA,
              sparkline_b: loc.sparklineB,
            });
            const apiId = (created.data as { data?: { id?: number } })?.data?.id;
            if (apiId) {
              await getPlatformStore().saveComparison(
                { ...loc, apiId } as unknown as Record<string, unknown>,
              );
            }
          } catch {
            /* skip sync for this item */
          }
        }
      }
    } catch {
      /* offline — local only */
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      merged = merged.filter(
        i => i.name.toLowerCase().includes(q) || i.tags.some(t => t.toLowerCase().includes(q)),
      );
    }

    setItems(merged);
    setLoading(false);
  }, [userId, search]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const handleDelete = async (id: string, apiId?: number) => {
    await getPlatformStore().deleteSavedComparison(id);
    if (apiId) {
      try {
        await comparisonsApi.delete(apiId);
      } catch {
        /* ignore */
      }
    }
    void load();
  };

  const handleShare = async (apiId?: number) => {
    if (!apiId) {
      window.alert(t('compare.saved.shareCloudFirst'));
      return;
    }
    try {
      const res = await comparisonsApi.enableShare(apiId);
      const token = (res.data as { data?: { share_token?: string } })?.data?.share_token;
      const base = (await getPlatformStore().getApiUrl()) || 'https://api.vo2peaks.com';
      const url = `${base.replace(/\/$/, '')}/api/v1/mobile/comparisons/shared/${token}`;
      await navigator.clipboard.writeText(url);
      if (token) setSharedToken(token);
      window.alert(t('compare.saved.shareCopied'));
    } catch {
      window.alert(t('compare.saved.shareFailed'));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <NmInput value={search} onChange={e => setSearch(e.target.value)} placeholder={t('compare.saved.searchPlaceholder')} />
        <NmButton variant="secondary" size="sm" onClick={() => void load()}>
          {t('compare.saved.refresh')}
        </NmButton>
      </div>
      {loading && <p style={{ color: 'var(--fg-3)' }}><NmLoader size="sm" inline label={t('compare.saved.loading')} /></p>}
      {!loading && items.length === 0 && (
        <p style={{ color: 'var(--fg-3)' }}>{t('compare.saved.empty')}</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map(item => (
          <div
            key={item.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '130px 1fr auto',
              gap: 12,
              alignItems: 'center',
              padding: 14,
              borderRadius: 'var(--r-3)',
              boxShadow: 'var(--nm-raised-sm)',
              background: 'var(--surface-0)',
            }}
          >
            <MiniSparkline a={item.sparklineA} b={item.sparklineB} />
            <div>
              <div style={{ fontWeight: 700 }}>{item.name}</div>
              <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                <span className="ltr-island">{formatApiDateCompact(item.savedAt, i18n.language)}</span>
                {item.tags.length > 0 && ` · ${item.tags.join(', ')}`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <NmButton variant="primary" size="sm" onClick={() => onOpen(item)}>
                {t('compare.saved.open')}
              </NmButton>
              <NmButton variant="ghost" size="sm" onClick={() => void handleShare(item.apiId)}>
                {t('compare.share')}
              </NmButton>
              <NmButton variant="danger" size="sm" onClick={() => void handleDelete(item.id, item.apiId)}>
                {t('common.delete')}
              </NmButton>
            </div>
          </div>
        ))}
      </div>
      {sharedToken && (
        <SharedComparisonReport token={sharedToken} onClose={() => setSharedToken(null)} />
      )}
    </div>
  );
}
